import { useEffect } from 'react';

const TICK_MS = 1000;
const FLUSH_EVERY_S = 60;
const IDLE_MS = 5 * 60 * 1000;
const IFRAME_IDLE_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'pointermove', 'wheel', 'scroll', 'touchstart'];

const mediaPlaying = () =>
    Array.from(document.querySelectorAll<HTMLMediaElement>('video, audio')).some((m) => !m.paused && !m.ended);

const focusInIframe = () => document.hasFocus() && document.activeElement?.tagName === 'IFRAME';

function sendTime(lessonId: number, seconds: number) {
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL}/api/progress/lesson/${lessonId}/time`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ seconds }),
    }).catch(() => {});
}

export function useLessonTimer(lessonId: number): void {
    useEffect(() => {
        let lastActivity = Date.now();
        let buffered = 0;

        const markActive = () => { lastActivity = Date.now(); };
        const onBlur = () => { if (focusInIframe()) markActive(); };
        const flush = () => {
            if (buffered > 0) {
                sendTime(lessonId, buffered);
                buffered = 0;
            }
        };
        const isActive = () => {
            if (document.visibilityState !== 'visible') return false;
            const idle = Date.now() - lastActivity;
            return idle <= IDLE_MS || mediaPlaying() || (idle <= IFRAME_IDLE_MS && focusInIframe());
        };
        const onVisibility = () => { if (document.visibilityState !== 'visible') flush(); };

        const tick = setInterval(() => {
            if (!isActive()) return;
            buffered += 1;
            if (buffered >= FLUSH_EVERY_S) flush();
        }, TICK_MS);

        ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, markActive, { capture: true, passive: true }));
        window.addEventListener('blur', onBlur);
        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', flush);

        return () => {
            clearInterval(tick);
            ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, markActive, { capture: true }));
            window.removeEventListener('blur', onBlur);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', flush);
            flush();
        };
    }, [lessonId]);
}
